import { useEffect, useState } from 'react';
import type { CampaignConfig, Lead } from '../types';

/**
 * Custom-field Forms tab (AE-10743): renders the campaign's field_defs against the current lead's
 * data and saves edits via PATCH /api/lead/:id. Sensitive fields are masked. Read-only when there
 * is no lead.
 */
export function FormsTab({ lead, config, onSave }: {
  lead: Lead | null;
  config: CampaignConfig;
  onSave: (leadId: string, data: Record<string, unknown>) => Promise<void>;
}) {
  const defs = config.field_defs ?? [];
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft({}); }, [lead?.lead_id]);
  if (defs.length === 0) return <p className="hint">This campaign has no custom fields.</p>;

  const value = (name: string) => draft[name] ?? String(lead?.data[name] ?? '');
  const save = async () => {
    if (!lead) return;
    setBusy(true);
    try { await onSave(lead.lead_id, draft); setDraft({}); } finally { setBusy(false); }
  };
  return (
    <form id="forms-tab" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      {defs.map((d) => (
        <div key={d.name} className="c3">
          <label>{d.label}{d.required ? ' *' : ''}</label>
          {d.type === 'SELECT' && d.options
            ? <select disabled={!lead} value={value(d.name)} onChange={(e) => setDraft((s) => ({ ...s, [d.name]: e.target.value }))}>
                <option value=""></option>
                {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            : <input disabled={!lead} type={d.sensitive ? 'password' : 'text'} value={value(d.name)} onChange={(e) => setDraft((s) => ({ ...s, [d.name]: e.target.value }))} />}
        </div>
      ))}
      <div className="c6"><button className="primary" type="submit" disabled={!lead || busy || Object.keys(draft).length === 0}>{busy ? 'Saving…' : 'Save'}</button></div>
    </form>
  );
}
